import JSZip from 'jszip';
import { toast } from 'react-toastify';
import { NotificationMessage } from '../components/Notification/NotificationMessage';
import { BaseService } from './BaseService';

/**
 * Сервис для работы с логами и уведомлениями
 */
export class EventLogService extends BaseService {

	/**
	 * Запрос на получение списка логов
	 * @param {Integer} count Максимальная длина массива с результатами
	 */
	static async getLogs(count = 30) {
		const response = await this.baseHandle(`/events/getlogs/${count}`);

		if (response.status === 200)
			return Promise.resolve(response.data);

		throw !!response.data ? response.data : response.message;
	}

	/**
	 * Запрос на скрытие записи лога из истории уведомлений
	 * @param {GUID} id recid записи лога
	 */
	static async hideLog(id) {
		const response = await this.baseHandle(`/events/hidelog/${id}`, "POST");

		if (response.status === 200)
			return Promise.resolve(response.data);

		throw !!response.data ? response.data : response.message;
	}

	/**
	 * Запросы на запись ошибки в логи ( в таблицу и в файл на сервере ) 
	 * @param {String} message Сообщение
	 * @param {Error object} errorInfo Объект описывающий ошибку
	 */
	static async writeFrontendLog(message, errorInfo) {
		this.writeLog("ErrorBoundary", null, message, 4, errorInfo);

		const body = {
			level: "Critical",
			name: "ErrorBoundary",
			message: message,
			source: errorInfo.componentStack,
		};

		const response = await this.baseHandle("/frontendlog/writemessage", "POST", { "Content-Type": "application/json" }, body);

		if (response.status === 200)
			return Promise.resolve(response.data);

		throw !!response.data ? response.data : response.message;
	}

	/**
	 * Запрос на список лог-файлов
	 */
	static async getLogFiles() {
		const response = await this.baseHandle("/getlogfiles");

		if (response.status === 200) {
			const data = response.data.sort((a, b) => new Date(b.updated) - new Date(a.updated));
			return Promise.resolve(data);
		}

		throw !!response.data ? response.data : response.message;
	}

	/**
	 * Запрос на удаление файла лога
	 * @param {String} fileName имя файла
	 */
	static async deleteLogFile(fileName) {

		const response = await this.baseHandle(`/deleteLogFile/${fileName}`, "DELETE");

		if (response.status === 200)
			return Promise.resolve(true);

		throw !!response.data ? response.data : response.message;
	}

	/**
	 * Запрос на получение контента файла
	 * @param {String} fileName Имя лога файла
	 */
	static async getContentOfLogFile(fileName) {
		const response = await this.baseHandle(`/downloadLogFile/${fileName}`);

		if (response.status === 200) {
			const data = (await JSZip.loadAsync(response.data)).file(`${fileName}.log`);
			const content = await data.async("string");
			return Promise.resolve(content);
		}

		throw !!response.data ? response.data : response.message;
	}

	/**
	 * Функция делает запись в таблицу логов событий
	 * @param {string} objectName Наименование объект вызвавший событие 
	 * ( по умолчанию - RdevService)
	 * @param {string} message Сообщение
	 * @param {number} logLevel Уровень лога 
	 * 1 - INFO
	 * 2 - DEBUG
	 * 3 - WARNING
	 * 4 - ERROR
	 * @param {any} data Дополнительные данные
	 */
	static createLog(objectName = 'RdevService', senderID = '-', message, logLevel, data) {
		return this.writeLog(objectName, senderID, message, logLevel, data);
	}

	/**
	 * Создает уведомление (без записи в логи)
	 * @param {Boolean} isSuccess True - Успех, иначе False
	 * @param {string} message Текст уведомления
	 * @param {object} additionData Дополнительные данные
	 */
	static async createNotify(isSuccess, messages, additionData) {

		const toastId = this._generateId();
		const toastOptions = {
			className: isSuccess ? "bg-success " : "bg-danger",
			toastId: toastId,
			position: toast.POSITION.BOTTOM_RIGHT,
			autoClose: 5000,
			closeOnClick: false,
			pauseOnFocusLoss: false,
			draggable: false
		};
		const content = <NotificationMessage
			message={messages}
			dateTime={new Date(Date.now())}
			isSuccess={isSuccess}
			additionData={additionData}
			toastId={toastId}
		/>;
		const func = isSuccess ? 'success' : 'error';
		toast[func](content, toastOptions);
	}

	static _generateId = () => "id" + Math.random().toString(16).slice(2);
}
